import React, { useState } from 'react';
import { AnimatePresence, motion, useMotionTemplate, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { hubSpokes, hiddenHubSpokes } from '../data/hubSpokes';
import { Wordmark } from './Wordmark';
const RADIUS = 36;
const CENTER = 50;
const STEP = 360 / hubSpokes.length;
const ease = [0.23, 1, 0.32, 1] as const;
const smooth = {
  stiffness: 70,
  damping: 20,
  mass: 0.9
};
const slots = hubSpokes.map((_, i) => {
  const angle = (-90 + STEP * i) * Math.PI / 180;
  return {
    x: CENTER + RADIUS * Math.cos(angle),
    y: CENTER + RADIUS * Math.sin(angle)
  };
});
export function HubDiagram() {
  const reduceMotion = useReducedMotion();
  const [revealed, setRevealed] = useState(false);
  const tools = revealed ? hiddenHubSpokes : hubSpokes;

  // Pointer position, normalized to -0.5..0.5, smoothed with a soft spring
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, smooth);
  const sy = useSpring(my, smooth);

  // Wheel behind turns and drifts with the cursor
  const wheelRotate = useTransform(sx, [-0.5, 0.5], [-40, 40]);
  const counterRotate = useTransform(wheelRotate, r => -r);
  const wheelX = useTransform(sx, [-0.5, 0.5], [-18, 18]);
  const wheelY = useTransform(sy, [-0.5, 0.5], [-18, 18]);

  // Center sphere: light follows the cursor, meridians spin, sphere shifts slightly opposite for depth
  const hubX = useTransform(sx, [-0.5, 0.5], [6, -6]);
  const hubY = useTransform(sy, [-0.5, 0.5], [6, -6]);
  const lightX = useTransform(sx, [-0.5, 0.5], [22, 46]);
  const lightY = useTransform(sy, [-0.5, 0.5], [20, 44]);
  const sphereFill = useMotionTemplate`radial-gradient(circle at ${lightX}% ${lightY}%, #F26A7D 0%, #D4243C 28%, #B0142B 58%, #6E0A19 100%)`;
  const phase = useTransform(sx, [-0.5, 0.5], [-Math.PI / 2, Math.PI / 2]);
  const meridianA = useTransform(phase, p => 46 * Math.abs(Math.sin(p)));
  const meridianB = useTransform(phase, p => 46 * Math.abs(Math.sin(p + Math.PI / 3)));
  const meridianC = useTransform(phase, p => 46 * Math.abs(Math.sin(p + 2 * Math.PI / 3)));
  const equatorY = useTransform(sy, [-0.5, 0.5], [44, 56]);
  const handleMove = (e: React.PointerEvent<HTMLElement>) => {
    if (reduceMotion || e.pointerType !== 'mouse') return;
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => {
    setRevealed(false);
    mx.set(0);
    my.set(0);
  };
  const allLabels = Array.from(new Set([...hubSpokes, ...hiddenHubSpokes].map(s => s.label))).join(', ');
  return <div className="mx-auto w-full max-w-[540px]" data-id="element-47" data-mp="2ucvdzaeqb5nr">
      <figure tabIndex={0} role="button" aria-pressed={revealed} aria-label={`NEXT OS powers ${allLabels}`} onPointerEnter={e => e.pointerType === 'mouse' && setRevealed(true)} onPointerLeave={e => e.pointerType === 'mouse' && handleLeave()} onPointerUp={e => e.pointerType !== 'mouse' && setRevealed(v => !v)} onPointerMove={handleMove} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setRevealed(v => !v);
      }
    }} className="relative aspect-square w-full cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-4" data-id="element-48" data-mp="2petsyjxqsw9n">
        <motion.div className="absolute inset-0" style={{
        rotate: wheelRotate,
        x: wheelX,
        y: wheelY
      }} data-id="element-49" data-mp="c7awztc4x8qm">
          <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-50" data-mp="lyykjj5km5mt">
            <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" stroke="#E5E7EB" strokeWidth="0.3" strokeDasharray="1 1.2" data-id="element-51" data-mp="qy1pl3ljwbus" />
            {slots.map((s, i) => <line key={i} x1={CENTER} y1={CENTER} x2={s.x} y2={s.y} stroke="#E5E7EB" strokeWidth="0.4" data-id="element-52" data-mp="vypd3fakmak9" data-mpi={i} />)}
            {!reduceMotion && slots.map((s, i) => <motion.circle key={`pulse-${i}`} r="0.9" fill="#C1172F" initial={{
            cx: CENTER,
            cy: CENTER,
            opacity: 0
          }} animate={{
            cx: [CENTER, s.x],
            cy: [CENTER, s.y],
            opacity: [0, 1, 1, 0]
          }} transition={{
            duration: 2.2,
            ease: 'linear',
            repeat: Infinity,
            delay: i * 0.37,
            repeatDelay: 0.4
          }} data-id="element-53" data-mp="1xm1hcz6yadge" data-mpi={i} />)}
          </svg>

          {slots.map((s, i) => {
          const tool = tools[i];
          const Icon = tool.icon;
          return <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2" style={{
            left: `${s.x}%`,
            top: `${s.y}%`
          }} data-id="element-54" data-mp="1nqhneiby5oua" data-mpi={i}>
                <motion.div style={{
              rotate: counterRotate
            }} data-id="element-55" data-mp="2z3sj7bxn5vst">
                  <AnimatePresence mode="wait" initial={false} data-id="element-56" data-mp="2z6elox5odl46">
                    <motion.div key={tool.label} initial={{
                  opacity: 0,
                  scale: 0.96
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.96
                }} transition={{
                  duration: 0.2,
                  ease,
                  delay: revealed ? i * 0.04 : 0
                }} className="flex w-28 flex-col items-center gap-2 text-center sm:w-32" data-id="element-57" data-mp="2u4z1f7bbnimu">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-full border shadow-[0_8px_20px_-10px_rgba(29,36,51,0.3)] transition-colors duration-200 ease-out sm:h-14 sm:w-14 ${revealed ? 'border-brand bg-brand text-white' : 'border-line bg-white text-brand'}`} data-id="element-58" data-mp="3ld92uxm0477h">
                        <Icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" data-id="element-59" data-mp="86gymy4cqvd5" />
                      </span>
                      <span className="whitespace-nowrap rounded-full bg-white px-1.5 text-xs font-semibold text-ink sm:text-sm" data-id="element-60" data-mp="cxpbk44w4yv2">
                        {tool.label}
                      </span>
                    </motion.div>
                  </AnimatePresence>
                </motion.div>
              </div>;
        })}
        </motion.div>

        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[30%] w-[30%] -translate-x-1/2 -translate-y-1/2" data-id="element-61" data-mp="ct6t3d1e80bf">
        <motion.div className="relative h-full w-full" style={{
          x: hubX,
          y: hubY
        }} data-id="element-62" data-mp="3ubizpv8a62u">
          <div className="absolute -bottom-[14%] left-[12%] h-[14%] w-[76%] rounded-[50%] bg-ink/25 blur-md" aria-hidden="true" data-id="element-63" data-mp="3wegeuypn1hl" />
          <motion.div className="absolute -inset-3 rounded-full border border-dashed border-brand/40" style={{
            rotate: wheelRotate
          }} aria-hidden="true" data-id="element-64" data-mp="3qao5l6agsdjf" />
          <motion.div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full shadow-[inset_-10px_-14px_24px_rgba(60,4,14,0.45),inset_6px_8px_16px_rgba(255,255,255,0.18),0_18px_40px_-16px_rgba(110,10,25,0.6)]" style={{
            background: sphereFill
          }} data-id="element-65" data-mp="3h7wsz35urbq7">
            <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-66" data-mp="1wpck7o385gjc">
              {[meridianA, meridianB, meridianC].map((rx, i) => <motion.ellipse key={i} cx="50" cy="50" ry="49" rx={rx} fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="0.8" data-id="element-67" data-mp="25qkezw6j7z8o" data-mpi={i} />)}
              <motion.ellipse cx="50" rx="49" ry="11" cy={equatorY} fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="0.8" data-id="element-68" data-mp="1i0q4hl9oqmrl" />
            </svg>
            <span className="relative [text-shadow:0_2px_6px_rgba(60,4,14,0.45)]" data-id="element-69" data-mp="ubx6zkdf06uv">
              <Wordmark inverted data-id="element-70" data-mp="3ju8sg2gmxk05" />
            </span>
          </motion.div>
        </motion.div>
        </div>
      </figure>

      <p className="mt-2 text-center text-xs text-ink-subtle" aria-hidden="true" data-id="element-71" data-mp="3ewz5ctgpxkfh">
        {revealed ? 'And there’s more coming.' : 'Hover to see more'}
      </p>
    </div>;
}